import { z } from "./zod.js";

// The product locale registry (D-430, which supersedes D-122). Every locale
// set in the contract, the API, the web runtime and the CI gates is derived
// from SUPPORTED_LOCALES. A hard-coded list anywhere else is refused by
// tools/ci/locale-literal-guard.ts, so adding a language is one entry here
// plus its translations.
//
// `zh` stays and means Simplified Chinese. Renaming it to `zh-Hans` would
// rewrite every stored `*_i18n` bundle, 255 pack records and the terminology
// table, and remove an enum value from /v1. Where a standard tag matters
// (`<html lang>`, font selection) the meta below maps it to `zh-Hans`.
// `zh-Hant` is Traditional Chinese with Taiwan vocabulary, translated from
// English and never converted from `zh`.

/** Every locale the product can be used in, in the order the switcher lists them. */
export const SUPPORTED_LOCALES = ["en", "ru", "fr", "zh", "zh-Hant", "hi"] as const;

/** The zod enum every locale-valued contract field derives from. */
export const Locale = z.enum(SUPPORTED_LOCALES);
export type Locale = (typeof SUPPORTED_LOCALES)[number];

/** The source locale. Every dictionary and every server string is complete in it. */
export const SOURCE_LOCALE = "en" satisfies Locale;

/** Every locale but the source, in registry order: the keys a bundle copies
 * after its required `en`. */
export const TRANSLATED_LOCALES: readonly Exclude<Locale, typeof SOURCE_LOCALE>[] =
  SUPPORTED_LOCALES.filter(
    (locale): locale is Exclude<Locale, typeof SOURCE_LOCALE> => locale !== SOURCE_LOCALE,
  );

export interface LocaleMeta {
  /** The language's name in itself, as the switcher shows it. */
  readonly endonym: string;
  /** The BCP 47 tag written to `<html lang>` and used for font selection. */
  readonly htmlLang: string;
  /** ISO 15924 script code. The parity gate checks translated values against it. */
  readonly script: "Latn" | "Cyrl" | "Hans" | "Hant" | "Deva";
  readonly direction: "ltr" | "rtl";
}

export const LOCALE_META: Readonly<Record<Locale, LocaleMeta>> = Object.freeze({
  en: { endonym: "English", htmlLang: "en", script: "Latn", direction: "ltr" },
  ru: { endonym: "Русский", htmlLang: "ru", script: "Cyrl", direction: "ltr" },
  fr: { endonym: "Français", htmlLang: "fr", script: "Latn", direction: "ltr" },
  zh: { endonym: "简体中文", htmlLang: "zh-Hans", script: "Hans", direction: "ltr" },
  "zh-Hant": { endonym: "繁體中文", htmlLang: "zh-Hant", script: "Hant", direction: "ltr" },
  hi: { endonym: "हिन्दी", htmlLang: "hi", script: "Deva", direction: "ltr" },
});

const SUPPORTED: ReadonlySet<string> = new Set(SUPPORTED_LOCALES);

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && SUPPORTED.has(value);
}

/**
 * Maps any BCP 47 tag a browser, a Telegram client or a stored row offers to a
 * supported locale, or undefined when none fits. Matching is case-insensitive
 * and never truncates Chinese to its primary subtag: `zh-TW`, `zh-HK`, `zh-MO`
 * and any `zh-Hant*` tag are Traditional, every other `zh*` tag is Simplified.
 * For the rest the exact tag wins, then the primary subtag (`hi-IN` -> `hi`,
 * `fr-SN` -> `fr`, `ru-KZ` -> `ru`).
 */
export function normalizeLocale(tag: string | null | undefined): Locale | undefined {
  if (typeof tag !== "string") return undefined;
  const trimmed = tag.trim().replace(/_/g, "-");
  if (trimmed === "") return undefined;
  const parts = trimmed.toLowerCase().split("-");
  const primary = parts[0];
  if (primary === "zh") {
    const rest = parts.slice(1);
    if (rest.includes("hant")) return "zh-Hant";
    if (rest.includes("hans")) return "zh";
    if (rest.some((p) => p === "tw" || p === "hk" || p === "mo")) return "zh-Hant";
    return "zh";
  }
  for (const locale of SUPPORTED_LOCALES) {
    if (locale.toLowerCase() === trimmed.toLowerCase()) return locale;
  }
  return isLocale(primary) ? primary : undefined;
}

/**
 * One zod schema per supported locale, for the `*_i18n` bundles. Callers
 * spread it and then override the keys their schema requires, so a new locale
 * reaches every bundle as an optional key (an additive /v1 change):
 *
 *   z.object({ ...localeKeys(z.string().max(200).optional()), en: z.string().min(1).max(200) })
 */
export function localeKeys<V extends z.ZodType>(value: V): Record<Locale, V> {
  const shape = {} as Record<Locale, V>;
  for (const locale of SUPPORTED_LOCALES) shape[locale] = value;
  return shape;
}

/**
 * Locales whose server-minted text (email, Telegram replies, PDF chrome, CoA
 * labels) is not written yet. A user whose locale is listed here reads that
 * text in English until it is. Server text maps are typed over
 * `ServerTextLocale`, so emptying this list makes tsc name every map that
 * still lacks an entry.
 */
export const SERVER_TEXT_PENDING = [] as const satisfies readonly Locale[];
export type ServerTextLocale = Exclude<Locale, (typeof SERVER_TEXT_PENDING)[number]>;

/** True when server text is written in `locale`. */
export function isServerTextLocale(locale: Locale): locale is ServerTextLocale {
  return !(SERVER_TEXT_PENDING as readonly Locale[]).includes(locale);
}

/** The locales server text is written in, in registry order. */
export const SERVER_TEXT_LOCALES: readonly ServerTextLocale[] =
  SUPPORTED_LOCALES.filter(isServerTextLocale);

/**
 * The language a public customer page (quote, order tracking) is served in.
 * The page's words are the web `public` namespace plus server-made document
 * text, so a customer whose locale has no such text yet reads the page in
 * English: `serverTextLocale` maps it. This enum widens with SERVER_TEXT_LOCALES.
 */
export const PublicPageLocale = z.enum(
  SERVER_TEXT_LOCALES as unknown as readonly [ServerTextLocale, ...ServerTextLocale[]],
);

/** The locale server text renders in for a user whose locale is `locale`. */
export function serverTextLocale(locale: Locale): ServerTextLocale {
  return isServerTextLocale(locale) ? locale : "en";
}

/**
 * A parsed `*_i18n` bundle with its absent locales dropped, for writers that
 * store `Record<string, string>` (an optional zod key parses to `undefined`,
 * which jsonb would store as `null`).
 */
export function compactLocaleBundle(
  bundle: Readonly<Partial<Record<Locale, string | undefined>>>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const locale of SUPPORTED_LOCALES) {
    const value = bundle[locale];
    if (value !== undefined) out[locale] = value;
  }
  return out;
}
